<template>
  <div class="input-screen-page-wrapper" style="width: 100%; height: 100%">
    <!-- Menu options -->

    <inputs-grid
      ref="inputsGrid"
      :additional-columns="additionalColumns"
      :additional-product-columns="additionalProductColumns"
      :additional-grid-options="additionalGridOptions"
      :additional-dependent-columns="dependentColumns"
      :column-validations="columnValidations"
      :save-changes="saveChanges"
      :delete="openDeleteDialog"
      :export-action="downloadItems"
      :is-exporting="downloadingItems"
      :is-saveable="!runningMarginInitialisation"
      :is-not-saveable-reason="$t('tooltip.saveDisabledMarginInitialisationRunningMessage')"
      route-suffix="competitor-matches"
    >
      <!-- Slots for inputs-grid, -->
      <!-- Import/ Add buttons etc. -->
      <template #buttons>
        <feature-toggle :toggle="enableCompetitorSimulation">
          <div class="workflow-controls">
            <div class="d-flex workflow-buttons">
              <v-btn
                color="primary"
                class="pa-0"
                small
                depressed
                :loading="runningCompetitorPriceSimulation || refreshing"
                @click="
                  runCompetitorPriceSimulation({
                    workpackageId: selectedWorkpackage._id,
                  })
                "
              >
                {{ $t('actions.runSimulation') }}
              </v-btn>
            </div>
            <div class="workflow-info-row">
              <last-triggered-info
                :last-triggered-info="lastTriggeredInfo"
                :loading="isLastTriggeredLoading"
                :translation-time-key="'competitorMatches.editor.simulationLastRunTime'"
                :width="200"
              >
                <template #extrainfo>
                  <feature-toggle toggle="enableReferenceDate">
                    <div>
                      {{ $t('competitorMatches.editor.simulationUsedReferenceDate') }}
                      {{ referenceDateUsedBySimulation }}
                    </div>
                  </feature-toggle>
                </template>
              </last-triggered-info>
            </div>
          </div>
        </feature-toggle>
        <!-- FEATURE_FLAG: display competitor add button -->
        <feature-toggle :toggle="displayAddCompetitorButton">
          <v-btn
            color="primary"
            class="pl-0"
            small
            depressed
            @click="openCompetitorCreateUpdateDialog(dialogModes.create)"
          >
            <v-icon>mdi-plus</v-icon>
            <span class="pl-1">{{ $t('actions.add') }}</span>
          </v-btn>
        </feature-toggle>

        <feature-toggle :toggle="displayCompetitorUpload">
          <competitor-upload
            :enabled-value-translations="enabledValueTranslations"
            :is-master-workpackage="isSelectedWorkpackageMaster"
          />
        </feature-toggle>
      </template>
    </inputs-grid>

    <!-- Create/Update dialog -->
    <competitor-create-update-dialog
      :key="competitorCreateDialogKey"
      :is-open="isCompetitorCreateUpdateDialogOpen"
      :mode="mode"
      :competitor="competitorToEdit"
      @closeDialog="closeCompetitorCreateUpdateDialog"
    />

    <!-- Delete dialog -->
    <alert-dialog
      :is-open="isDeleteDialogOpen"
      :ok-btn-text="$t('actions.delete')"
      :disable-ok-button="checkingIfCompetitorCanBeDeleted || !competitorCanBeDeleted"
      @onOk="deleteCompetitorHandler(true)"
      @onCancel="deleteCompetitorHandler(false)"
    >
      <template v-slot:header>{{ $t('dialogs.deleteCompetitorDialog.header') }}</template>
      <template v-slot:body>
        <div v-if="!checkingIfCompetitorCanBeDeleted && competitorCanBeDeleted">
          {{ $t('dialogs.deleteCompetitorDialog.body') }}
          <strong>{{
            competitorToDelete ? competitorToDelete.competitorDisplayDescription : ''
          }}</strong>
        </div>
        <div v-else-if="checkingIfCompetitorCanBeDeleted && competitorUses.length === 0">
          {{ $t('competitorMatches.deleteMessages.checkingIfTheCompetitor') }}
          <strong>{{
            competitorToDelete ? competitorToDelete.competitorDisplayDescription : ''
          }}</strong>
          {{ $t('competitorMatches.deleteMessages.canBeDeleted') }}
          <v-progress-circular indeterminate color="primary" size="20" />
        </div>
        <div v-else>
          <div class="pb-1">
            <strong>{{
              competitorToDelete ? competitorToDelete.competitorDisplayDescription : ''
            }}</strong>
            {{ $t('competitorMatches.deleteMessages.competitorCannotBeDeleted') }}:
          </div>
          <div v-for="(competitorUse, index) in competitorUses" :key="index">
            <div class="pl-2">
              <v-icon class="pb-1" size="1.7rem" :color="alertsIconColor">warning</v-icon>
              {{
                $t(
                  `competitorMatches.deleteMessages.${competitorUse.translationKey}`,
                  competitorUse.translationParams
                )
              }}
            </div>
          </div>
        </div>
      </template>
    </alert-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import axios from 'axios';
import {
  get,
  startCase,
  set,
  flatMap,
  isUndefined,
  isEmpty,
  isNil,
  isNull,
  isNumber,
  round,
} from 'lodash';
import { categoryLevel, pricingGroupLevel, architectureGroupLevel } from '@enums/hierarchy';
import {
  displayCompetitorUpload,
  displayAddCompetitorButton,
  displayCompetitorPromotion,
  displayHierarchyAndFilter,
  displayClientProductKey,
  displayCompetitorHistoricalPrices,
  useZones,
  enableCompetitorSimulation,
  enableReferenceDate,
} from '@enums/feature-flags';
import dialogModes from '@enums/dialog-modes';
import DataTypes from '@enums/data-types';
import { inputTableCssClasses } from '@enums/tables';
import numberFormats from '@enums/number-formats';
import { competitorSources } from '@enums/source-types';
import Events from '@enums/events';
import { descending } from '@enums/sort-direction';
import featureFlagsMixin from '../../../mixins/featureFlags';
import tablesMixin from '../../../mixins/tables';
import inputScreensTableMixin from '../../../mixins/inputScreensTable';
import marginInitialisationMixin from '../../../mixins/marginInitialisation';
import checkboxCellRenderer from './components/checkbox-cell-renderer.vue';
import { localisedNumericCol, localisedDateCol } from '../../../utils/inputs-grid-utils';
import { getValueFromAuditLogByKey } from '@/js/utils/audit-log';

const requiredIfEnabled = {
  message: 'competitorPriceRequiredIfEnabled',
  test({ node, column, value }) {
    const [, competitorType, competitorKey] = column.colId.split('::');
    const isEnabled = !get(
      node,
      `data.competitor.${competitorType}.${competitorKey}.disabled`,
      true
    );
    // If the competitor is enabled, the value must not be null or undefined
    if (isEnabled) {
      return value != null;
    }

    // If the competitor is not enabled, the value is valid regardless
    return true;
  },
};

export default {
  name: 'CompetitorMatchesEditor',
  components: {
    // register vue components
    // eslint thinks that it's not used, but headerComponent points to it
    // eslint-disable-next-line vue/no-unused-components
    checkboxCellRenderer,
  },
  mixins: [featureFlagsMixin, tablesMixin, inputScreensTableMixin, marginInitialisationMixin],

  async beforeRouteLeave(destination, from, next) {
    const preventNavigation = await this.$refs.inputsGrid.resolveEditsBeforeNavigation();

    if (preventNavigation) return;

    next();
  },

  data() {
    return {
      get,
      startCase,
      isCompetitorCreateUpdateDialogOpen: false,
      mode: dialogModes.create,
      dialogModes,
      displayCompetitorUpload,
      displayAddCompetitorButton,
      enableCompetitorSimulation,
      competitorToEdit: null,
      isDeleteDialogOpen: false,
      competitorToDelete: null,
      lastTriggeredInfo: {},
      isLastTriggeredLoading: false,
      runningCompetitorPriceSimulation: false,
      checkingIfCompetitorCanBeDeleted: false,
      competitorUses: [],
      columnValidations: {},
      dependentColumns: {},
      additionalGridOptions: {
        getMainMenuItems: this.getColumnMenuItems,
        enableFillHandle: false,
      },
      enabledValueTranslations: {
        1: this.$t('competitorMatches.editor.enabledTrue'),
        0: this.$t('competitorMatches.editor.enabledFalse'),
      },
      additionalProductColumns: [
        {
          field: 'productSizeType',
          tooltipValueGetter: params => ({ additionalText: [params.data.productSizeType] }),
          colId: 'productSizeType',
          headerName: this.$t('competitorMatches.editor.productSize'),
          sortable: true,
          editable: false,
          pinned: 'left',
          cellClass: 'bold-text',
          valueFormatter: params =>
            this.formatMetricContainedUnitOfMeasurement(params.value, numberFormats.weight),
        },
      ],
    };
  },

  computed: {
    ...mapState('workpackages', ['selectedWorkpackage', 'refreshingIds']),
    ...mapState('workpackageProducts', ['downloadingItems']),
    ...mapState('hierarchy', {
      loadingHierarchy: 'loading',
      busyImportingGroup: 'busyImportingGroup',
    }),
    ...mapState('competitorMetadata', ['competitorKeyToTypeMap']),
    ...mapGetters('competitorMetadata', ['competitors']),
    ...mapGetters('context', ['isPricingSpecialist']),
    ...mapState('filters', ['retailAttributesFilter']),
    ...mapGetters('workpackages', ['isSelectedWorkpackageMaster']),
    ...mapState('clientConfig', ['exportConfigs']),

    refreshing() {
      return this.refreshingIds.includes(this.selectedWorkpackage._id);
    },

    enableReferenceDate() {
      return this.isFeatureFlagEnabled(enableReferenceDate);
    },

    referenceDateUsedBySimulation() {
      if (!this.enableReferenceDate) return null;

      const referenceDate = getValueFromAuditLogByKey({
        auditLogEntry: this.lastTriggeredInfo,
        key: 'referenceDate',
      });
      return referenceDate ? this.formatLocalisedDate(referenceDate) : referenceDate;
    },

    showHierarchy() {
      return this.isFeatureFlagEnabled(displayHierarchyAndFilter);
    },

    exportTranslationMap() {
      const hierarchyColumnTranslations = this.showHierarchy
        ? {
            [`hierarchy.${categoryLevel}.levelEntryDescription`]: this.$t('pricing.category'),
            [`hierarchy.${pricingGroupLevel}.levelEntryDescription`]: this.$t(
              'pricing.pricingGroup'
            ),
            [`hierarchy.${architectureGroupLevel}.levelEntryDescription`]: this.$t(
              'pricing.architectureGroup'
            ),
          }
        : {};

      const toolStoreGroupColumn = this.zonesEnabled
        ? { toolStoreGroupKey: this.$t('pricing.toolStoreGroup') }
        : {};

      const clientProductKeyColumn = this.isFeatureFlagEnabled(displayClientProductKey)
        ? { clientProductKey: this.$t('pricing.clientProductKey') }
        : {};

      return {
        productKeyDisplay: this.$t('competitorMatches.editor.productKey'),
        ...clientProductKeyColumn,
        ...toolStoreGroupColumn,
        productName: this.$t('competitorMatches.editor.productDescription'),
        productSizeType: this.$t('competitorMatches.editor.productSize'),
        competitorName: this.$t('competitorMatches.editor.competitorName'),
        competitorPrice: this.$t('competitorMatches.editor.price'),
        // TODO PRICE-4961: export simulation fields when feature flag is enabled
        // ...(this.isFeatureFlagEnabled(enableCompetitorSimulation) && {
        //   simulatedCompetitorPriceWithPP: this.$t('competitorMatches.editor.simulatedPrice'),
        //   simulationDate: this.$t('competitorMatches.editor.simulationDate'),
        // }),
        competitorEnabled: this.$t('competitorMatches.editor.enabled'),
        ...hierarchyColumnTranslations,
      };
    },

    hierarchyHeaders() {
      return this.showHierarchy
        ? [
            {
              text: this.$t('pricing.category'),
              align: 'start',
              sortable: true,
              value: `hierarchy.${categoryLevel}.levelEntryDescription`,
              class: 'm-width',
              dataType: DataTypes.str,
            },
            {
              text: this.$t('pricing.pricingGroup'),
              align: 'start',
              sortable: true,
              value: `hierarchy.${pricingGroupLevel}.levelEntryDescription`,
              class: 'm-width',
              dataType: DataTypes.str,
            },
            {
              text: this.$t('pricing.architectureGroup'),
              align: 'start',
              sortable: true,
              value: `hierarchy.${architectureGroupLevel}.levelEntryDescription`,
              class: 'border-right m-width',
              dataType: DataTypes.str,
            },
          ]
        : [];
    },

    competitorCreateDialogKey() {
      const competitorDisplayDescription = get(
        this.competitorToEdit,
        'competitorDisplayDescription',
        ''
      );
      return `competitorCreateDialogKey-${competitorDisplayDescription}`;
    },

    isUseZones() {
      return this.isFeatureFlagEnabled(useZones);
    },

    toolStoreGroupColumn() {
      return this.isUseZones
        ? {
            ...this.toolStoreGroupColumnDefaultProperties,
            text: this.$t('competitorMatches.editor.toolStoreGroup'),
            class: `m-width ${inputTableCssClasses.fourFixedColumns}`,
          }
        : null;
    },

    competitorCanBeDeleted() {
      return isEmpty(this.competitorUses);
    },

    staticHeaders() {
      const tsgHeaders = this.toolStoreGroupColumn ? [this.toolStoreGroupColumn] : [];
      return [
        {
          text: this.$t('competitorMatches.editor.productKey'),
          align: 'start',
          sortable: true,
          value: 'productKeyDisplay',
          class: `sortable s-width ${this.fixedColumnsClass}`,
          dataType: DataTypes.str,
          formatter: {
            type: get(
              this.exportConfigs,
              'exportToExcel.columnFormatter.productKeyDisplay',
              numberFormats.integer
            ),
          },
        },
        ...tsgHeaders,
        {
          text: this.$t('competitorMatches.editor.productDescription'),
          align: 'start',
          sortable: true,
          value: 'productName',
          class: `sortable l-width ${this.fixedColumnsClass}`,
          dataType: DataTypes.str,
        },
        {
          text: this.$t('competitorMatches.editor.productSize'),
          align: 'start',
          sortable: true,
          value: 'productSizeType',
          class: `sortable border-right s-width ${this.fixedColumnsClass}`,
          dataType: DataTypes.str,
        },
      ];
    },

    headers() {
      const competitorHeaders = flatMap(this.competitors, (c, i) => {
        return [
          {
            text: this.$t('competitorMatches.editor.price'),
            sortable: true,
            align: 'start',
            key: 'competitorPrice',
            competitorKey: c.competitorKey,
            value: `competitor.${c.competitorType}.${c.competitorKey}.competitorPrice`,
            competitor: true,
            class: 'sortable',
            competitorType: c.competitorType,
            dataType: DataTypes.number,
          },
          ...(this.canDisplaySimulatedPrice(c)
            ? [
                {
                  text: this.$t('competitorMatches.editor.simulatedPrice'),
                  sortable: true,
                  editable: false,
                  align: 'start',
                  key: 'simulatedCompetitorPriceWithPP',
                  competitorKey: c.competitorKey,
                  value: `competitor.${c.competitorType}.${
                    c.competitorKey
                  }.simulatedCompetitorPriceWithPP`,
                  competitor: true,
                  class: 'sortable',
                  competitorType: c.competitorType,
                  dataType: DataTypes.number,
                },
                {
                  text: this.$t('competitorMatches.editor.simulationDate'),
                  sortable: true,
                  editable: false,
                  align: 'start',
                  key: 'simulationDate',
                  competitorKey: c.competitorKey,
                  value: `competitor.${c.competitorType}.${c.competitorKey}.simulationTimestamp`,
                  competitor: true,
                  class: 'sortable',
                  competitorType: c.competitorType,
                  // match priceOverrides logic. we use unix timestamps, which are numbers in the database.
                  isDate: true,
                  dataType: DataTypes.number,
                  // TODO PRICE-4961: Uncomment this when enabling export of simulation fields
                  // formatter: {
                  //   type: DataTypes.date,
                  //   format: this.dateFormats.clientDateFormatForMoment,
                  // },
                },
              ]
            : []),
          {
            text: this.$t('competitorMatches.editor.enabled'),
            align: 'start',
            key: `competitor[${c.competitorDescription}].disabled`,
            value: `competitor.${i}.disabled`,
            competitor: true,
            class: 'border-right',
            competitorType: c.competitorType,
            dataType: DataTypes.boolean,
          },
        ];
      });

      return [...this.staticHeaders, ...this.hierarchyHeaders, ...competitorHeaders];
    },

    zonesEnabled() {
      return this.isFeatureFlagEnabled(useZones);
    },

    additionalColumns() {
      const showHistoricalCompetitorPrices = this.isFeatureFlagEnabled(
        displayCompetitorHistoricalPrices
      );
      const mapCompetitor = (competitor, competitorIndex) => ({
        headerName: competitor.competitorDisplayDescription,
        groupId: `competitor::${competitor.competitorType}::${competitor.competitorKey}`,
        ...(competitorIndex !== 0 && { columnGroupShow: 'open' }),
        children: [
          {
            headerName: `${this.$t('competitorMatches.editor.price')} (${this.$t(
              'clientCurrencySymbol'
            )})`,
            headerTooltip: `${this.$t('competitorMatches.editor.price')} (${this.$t(
              'clientCurrencySymbol'
            )})`,
            colId: `competitor::${competitor.competitorType}::${
              competitor.competitorKey
            }::competitorPrice`,
            field: `competitor.${competitor.competitorType}.${
              competitor.competitorKey
            }.competitorPrice`,
            cellDataType: 'number',
            cellClass: 'left-competitor-border',
            headerClass: 'left-competitor-border',
            onCellValueChanged: this.competitorPriceChangeHandler,
            editable: true,
            onCellClicked: params => {
              // We need to prepopulate with undefined; otherwise, the values won't be retained on UI
              return this.populateEmptyCompetitorsData(params, undefined);
            },
            tooltipValueGetter: params => {
              const competitorData = get(
                params.data,
                `competitor.${competitor.competitorType}.${competitor.competitorKey}`
              );
              const { competitorProductDescription, contentValue, contentUnitOfMeasure } =
                competitorData || {};

              const previousValueText = this.tooltipValueGetterPreviousValue(params);

              const unknownValueText = this.$t('tooltip.unknownValueText');
              const competitorProductDescriptionText = `${this.$t(
                'tooltip.competitorDescription'
              )}: ${competitorProductDescription || unknownValueText}`;

              const competitorSizeText = `${this.$t('tooltip.competitorSize')}: ${contentValue ||
                unknownValueText}`;
              const competitorUoMText = `${this.$t(
                'tooltip.competitorUnitOfMeasure'
              )}: ${contentUnitOfMeasure || unknownValueText}`;

              const additionalText = [
                competitorProductDescriptionText,
                competitorSizeText,
                competitorUoMText,
              ];

              // Add additional price information if available
              [
                'competitorPrice',
                'grossReferenceCompetitorPrice',
                'avgCompetitorPrice',
                'medianShelfCompetitorPrice',
                'minCompetitorPrice',
                'maxCompetitorPrice',
                'numberStoresMeasured',
              ].forEach(field => {
                if (!isNil(get(competitorData, field))) {
                  additionalText.push(`${this.$t(`tooltip.${field}`)}: ${competitorData[field]}`);
                }
              });

              return {
                ...previousValueText,
                additionalText,
              };
            },
            ...localisedNumericCol,
          },
          ...(showHistoricalCompetitorPrices && competitor.competitorType === 'feed'
            ? [
                'competitorPriceTwoWeeksAgo',
                'competitorPriceLastWeek',
                'competitorPriceThisWeek',
              ].map(colName => ({
                headerName: this.$t(`pricing.${colName}`),
                headerTooltip: this.$t(`pricing.${colName}`),
                colId: `competitor::${competitor.competitorType}::${
                  competitor.competitorKey
                }::${colName}`,
                field: `competitor.${competitor.competitorType}.${
                  competitor.competitorKey
                }.${colName}`,
                cellDataType: 'number',
                ...localisedNumericCol,
                editable: false,
              }))
            : []),
          ...(competitor.competitorType === 'feed' &&
          this.isFeatureFlagEnabled(displayCompetitorPromotion)
            ? [
                {
                  headerName: this.$t('pricing.competitorPromotion'),
                  colId: `competitor::${competitor.competitorType}::${
                    competitor.competitorKey
                  }::competitorPromotion`,
                  field: `competitor.${competitor.competitorType}.${
                    competitor.competitorKey
                  }.competitorPromotion`,
                  tooltipValueGetter: params => {
                    const promotion = get(
                      params.data,
                      `competitor.${competitor.competitorType}.${
                        competitor.competitorKey
                      }.competitorPromotion`
                    );
                    return promotion ? { additionalText: [promotion] } : null;
                  },
                  cellDataType: 'text',
                  editable: false,
                },
              ]
            : []),
          ...(this.enableReferenceDate
            ? [
                {
                  headerName: `${this.$t(
                    'competitorMatches.editor.competitorReferenceDatePrice'
                  )} (${this.$t('clientCurrencySymbol')})`,
                  headerTooltip: `${this.$t(
                    'competitorMatches.editor.competitorReferenceDatePrice'
                  )} (${this.$t('clientCurrencySymbol')})`,
                  colId: `competitor::${competitor.competitorType}::${
                    competitor.competitorKey
                  }::competitorReferenceDatePrice`,
                  field: `competitor.${competitor.competitorType}.${
                    competitor.competitorKey
                  }.competitorReferenceDatePrice`,
                  cellDataType: 'number',
                  ...localisedNumericCol,
                  editable: false,
                },
                {
                  headerName: `${this.$t(
                    'competitorMatches.editor.competitorReferenceDatePriceDelta'
                  )} (${this.$t('clientCurrencySymbol')})`,
                  headerTooltip: `${this.$t(
                    'competitorMatches.editor.competitorReferenceDatePriceDeltaExplanation'
                  )} (${this.$t('clientCurrencySymbol')})`,
                  colId: `competitor::${competitor.competitorType}::${
                    competitor.competitorKey
                  }::competitorReferenceDatePriceDelta`,
                  // no field, this is a derived column from competitorPrice - competitorReferenceDatePrice
                  valueGetter: params => {
                    const competitorData = get(
                      params.data,
                      `competitor.${competitor.competitorType}.${competitor.competitorKey}`
                    );
                    const competitorPrice = get(competitorData, 'competitorPrice');
                    const competitorReferenceDatePrice = get(
                      competitorData,
                      'competitorReferenceDatePrice'
                    );
                    if (isNil(competitorPrice) || isNil(competitorReferenceDatePrice)) {
                      return null;
                    }
                    return competitorPrice - competitorReferenceDatePrice;
                  },
                  cellDataType: 'number',
                  ...localisedNumericCol,
                  editable: false,
                },
              ]
            : []),
          ...(this.canDisplaySimulatedPrice(competitor)
            ? [
                {
                  headerName: `${this.$t('competitorMatches.editor.simulatedPrice')} (${this.$t(
                    'clientCurrencySymbol'
                  )})`,
                  headerTooltip: `${this.$t('competitorMatches.editor.simulatedPrice')} (${this.$t(
                    'clientCurrencySymbol'
                  )})`,
                  colId: `competitor::${competitor.competitorType}::${
                    competitor.competitorKey
                  }::simulatedCompetitorPriceWithPP`,
                  field: `competitor.${competitor.competitorType}.${
                    competitor.competitorKey
                  }.simulatedCompetitorPriceWithPP`,
                  cellDataType: 'number',
                  tooltipValueGetter: params => {
                    const unknownValueText = this.$t('tooltip.unknownValueText');
                    // prices without pricePointing need to be rounded, they can have lots of floating point digits
                    const roundAndLocalise = value => {
                      if (!isNumber(value)) return value;

                      // round to 3 digits to show tie breakers rounding to 2 places for currency
                      const precision = 3;
                      return localisedNumericCol.valueFormatter({
                        value: round(value, precision),
                        // default maximumFractionDigits is 2, need greater precision while keeping client formatting
                        colDef: {
                          context: { numberFormatsConfig: { maximumFractionDigits: precision } },
                        },
                      });
                    };

                    const competitorData =
                      get(
                        params.data,
                        `competitor.${competitor.competitorType}.${competitor.competitorKey}`
                      ) || {};
                    const simulationInputs = get(competitorData, 'simulationInputs');
                    const simulatedCompetitorPrice = roundAndLocalise(
                      get(competitorData, 'simulatedCompetitorPrice', unknownValueText)
                    );

                    const competitorPrice = roundAndLocalise(
                      get(simulationInputs, 'competitorPrice', unknownValueText)
                    );
                    const costDeltaFactor = roundAndLocalise(
                      get(simulationInputs, 'costDeltaFactor', unknownValueText)
                    );
                    const goLiveCost = roundAndLocalise(
                      get(simulationInputs, 'goLiveCost', unknownValueText)
                    );
                    const liveCost = roundAndLocalise(
                      get(simulationInputs, 'liveCost', unknownValueText)
                    );

                    return {
                      additionalText: [
                        `${this.$t(
                          'tooltip.simulatedPriceWithoutPricePointing'
                        )}: ${simulatedCompetitorPrice}`,
                        `${this.$t('tooltip.competitorPriceAtSimulation')}: ${competitorPrice}`,
                        `${this.$t('tooltip.costDeltaFactor')}: ${costDeltaFactor}`,
                        `${this.$t('tooltip.goLiveCost')}: ${goLiveCost}`,
                        `${this.$t('tooltip.liveCost')}: ${liveCost}`,
                      ],
                    };
                  },
                  ...localisedNumericCol,
                  // must set editable: false after localisedNumericCol
                  editable: false,
                },
                {
                  headerName: `${this.$t('competitorMatches.editor.simulationDate')} `,
                  headerTooltip: `${this.$t('competitorMatches.editor.simulationDate')}`,
                  colId: `competitor::${competitor.competitorType}::${
                    competitor.competitorKey
                  }::simulationDate`,
                  field: `competitor.${competitor.competitorType}.${
                    competitor.competitorKey
                  }.simulationDate`,
                  valueFormatter: params => this.formatLocalisedDate(params.value),
                  editable: false,
                  dataType: DataTypes.number,
                  ...localisedDateCol,
                },
              ]
            : []),
          {
            headerName: this.$t('competitorMatches.editor.enabled'),
            suppressFillHandle: true,
            colId: `competitor::${competitor.competitorType}::${
              competitor.competitorKey
            }::disabled`,
            field: `competitor.${competitor.competitorType}.${competitor.competitorKey}.disabled`,
            cellRenderer: 'checkboxCellRenderer',
            cellRendererParams: {
              negate: true,
              setter: (params, value) => {
                set(
                  params.node,
                  `data.competitor.${competitor.competitorType}.${
                    competitor.competitorKey
                  }.disabled`,
                  value
                );
              },
            },
            filter: 'agSetColumnFilter',
            filterParams: {
              suppressMiniFilter: true,
              valueFormatter: params => {
                if (params.value === null) return null;
                return (!params.value).toString();
              },
            },
          },
        ],
      });

      const res = [
        {
          headerName: this.$t('competitorMatches.tabs.feed'),
          groupId: `feedCompetitors`,
          openByDefault: true,
          children: this.competitors
            .filter(competitor => competitor.competitorType === 'feed')
            .map(mapCompetitor),
        },
        {
          headerName: this.$t('competitorMatches.tabs.manual'),
          groupId: `manualCompetitors`,
          openByDefault: true,
          children: this.competitors
            .filter(competitor => competitor.competitorType === 'manual')
            .map(mapCompetitor),
        },
      ];
      return res;
    },
  },

  watch: {
    competitors: {
      handler() {
        this.addDynamicValidations();
        this.addDynamicDependentColumns();
      },
    },
  },

  mounted() {
    // Listen for the event
    this.$eventBus.$on('inputsUploaded', this.runMarginInitialisation);
  },

  beforeDestroy() {
    // Unsubscribe from the event to prevent memory leaks
    this.$eventBus.$off('inputsUploaded');
  },

  created() {
    this.addDynamicValidations();
    this.addDynamicDependentColumns();
    if (this.isFeatureFlagEnabled(enableCompetitorSimulation)) {
      this.fetchLastTriggeredInfo();
    }
  },

  methods: {
    ...mapActions('workpackageProducts', ['downloadCompetitorMatches']),
    ...mapActions('competitorMetadata', ['deleteCompetitor']),
    ...mapActions('attributes', ['checkForCompetitorUses']),
    ...mapMutations('notifications', ['addNotification']),
    tooltipValueGetterPreviousValue(params) {
      return this.$refs.inputsGrid.tooltipValueGetter(params);
    },
    isMainbanner(toolStoreGroupKey) {
      // If not using zones, you only have mainbanner
      if (!this.zonesEnabled) return true;

      return toolStoreGroupKey === this.mainTsgKey;
    },

    canDisplaySimulatedPrice({ competitorType }) {
      // Simulated price is not available for storeGroup competitors
      return (
        this.isFeatureFlagEnabled(enableCompetitorSimulation) &&
        competitorType !== competitorSources.storeGroup
      );
    },

    async runCompetitorPriceSimulation({ workpackageId }) {
      try {
        // ensure no changes are pending before running the simulation
        const preventNavigation = await this.$refs.inputsGrid.resolveEditsBeforeNavigation();
        if (preventNavigation) return;

        this.$refs.inputsGrid.gridApi.showLoadingOverlay();
        this.runningCompetitorPriceSimulation = true;
        const { data } = await axios.post(
          `/api/competitor-metadata/workpackage/${workpackageId}/runCompetitorPriceSimulation`
        );

        // reload grid data after simulation
        await Promise.all([
          this.$refs.inputsGrid.fetchGridData(),
          await this.fetchLastTriggeredInfo(),
        ]);
        this.addNotification({
          id: `competitor_price_simulation-${Date.now()}`,
          jobStatus: 'finished',
          notificationPayload: `${this.$tc('product', data.total_update_count)} in ${
            this.selectedWorkpackage.description
          }`,
          baseTranslation: 'notifications.competitor_price_simulation',
        });
      } catch (error) {
        console.error('Error running competitor price simulation:', error);
        this.addNotification({
          id: `competitor_price_simulation-${Date.now()}`,
          jobStatus: 'failed',
          notificationPayload: this.selectedWorkpackage.description,
          baseTranslation: 'notifications.competitor_price_simulation',
        });
      } finally {
        this.runningCompetitorPriceSimulation = false;
        this.$refs.inputsGrid.gridApi.hideOverlay();
      }
    },

    async fetchLastTriggeredInfo() {
      try {
        const workpackageId = this.selectedWorkpackage._id;

        this.isLastTriggeredLoading = true;
        // Build query parameters
        const params = {
          where: {
            fullEventName: Events.competitor.simulationRun,
            $and: [{ entityIds: { $in: [workpackageId] } }],
          },
          sortBy: 'date',
          sortDirection: descending,
          limit: 1,
        };

        // Make API request
        const { data: auditLogs } = await axios.get('/api/audit-log', { params });

        this.lastTriggeredInfo = get(auditLogs, 0, {});
      } catch (error) {
        console.error('Error fetching last triggered info:', error);
        this.lastTriggeredInfo = {};
      } finally {
        this.isLastTriggeredLoading = false;
      }
    },

    addDynamicValidations() {
      const dynamicValidations = {};
      this.competitors.forEach(competitor => {
        const { competitorKey, competitorType } = competitor;
        dynamicValidations[`competitor::${competitorType}::${competitorKey}::competitorPrice`] = {
          requiredIfEnabled,
        };
      });
      this.columnValidations = dynamicValidations;
    },

    addDynamicDependentColumns() {
      const dynamicDependentColumns = {};
      this.competitors.forEach(competitor => {
        const { competitorKey, competitorType } = competitor;
        dynamicDependentColumns[`competitor::${competitorType}::${competitorKey}::disabled`] = [
          `competitor::${competitorType}::${competitorKey}::competitorPrice`,
        ];
      });
      this.dependentColumns = dynamicDependentColumns;
    },

    competitorPriceChangeHandler(params) {
      // uncheck competitor checkbox if price is cleared
      const {
        column: { colId },
        node: rowNode,
        newValue,
        oldValue,
      } = params;
      // Disable competitor only if the price field has been cleared of its numeric value.
      if (isNull(newValue) && !isNil(oldValue)) {
        const [, competitorType, competitorKey] = colId.split('::');
        rowNode.setDataValue(`competitor::${competitorType}::${competitorKey}::disabled`, true);
      }
    },

    async downloadItems() {
      this.downloadCompetitorMatches({
        where: this.retailAttributesFilter,
        translationMap: this.exportTranslationMap,
        pick: this.getColumnExportPickOptions(this.toolStoreGroupColumn),
        enabledValueTranslations: this.enabledValueTranslations,
        columnFormatters: this.getColumnFormatters(this.headers),
      });
    },

    openCompetitorCreateUpdateDialog(mode, competitor = null) {
      this.mode = mode;
      this.competitorToEdit = competitor;
      this.isCompetitorCreateUpdateDialogOpen = true;
    },

    closeCompetitorCreateUpdateDialog() {
      this.competitorToEdit = null;
      this.isCompetitorCreateUpdateDialogOpen = false;
    },
    populateEmptyCompetitorsData(params, defaultValue) {
      // Populate fields that did not exist on the original structure
      // passed in to ag-grid for the row being clicked
      if (isUndefined(get(params, `data.${params.colDef.field}`))) {
        set(params, `data.${params.colDef.field}`, defaultValue);
      }

      return params;
    },

    getManualCompetitorMenuActions(params) {
      const [, , competitorKey] = params.column.parent.groupId.split('::');
      const competitor = this.competitors.find(c => String(c.competitorKey) === competitorKey);
      const competitorMenuActions = [
        {
          name: this.$t('actions.edit'),
          action: () => this.openCompetitorCreateUpdateDialog(dialogModes.edit, competitor),
        },
        {
          name: this.$t('actions.delete'),
          action: () => this.openDeleteDialog(competitor),
        },
      ];

      return competitorMenuActions;
    },

    getColumnMenuItems(params) {
      return [
        ...params.defaultItems,
        ...(params.column.parent.parent.groupId === 'manualCompetitors'
          ? ['separator', ...this.getManualCompetitorMenuActions(params)]
          : []),
      ];
    },

    async deleteCompetitorHandler(shouldDelete = false) {
      if (shouldDelete) {
        await this.deleteCompetitor({
          competitorKey: this.competitorToDelete.competitorKey,
        });
      }
      this.competitorToDelete = null;
      this.isDeleteDialogOpen = false;
      this.competitorUses = [];
    },

    async openDeleteDialog(competitor) {
      this.checkingIfCompetitorCanBeDeleted = true;
      this.competitorToDelete = competitor;
      this.isDeleteDialogOpen = true;
      this.competitorUses = await this.checkForCompetitorUses({ competitor });
      this.checkingIfCompetitorCanBeDeleted = false;
    },

    async saveChanges({ updates, mainTsgKey }) {
      try {
        await axios.post(
          `/api/inputs/workpackage/${this.selectedWorkpackage._id}/competitor-matches`,
          { updates, mainTsgKey }
        );
        this.addNotification({
          id: `competitor_matches_update-${Date.now()}`,
          jobStatus: 'finished',
          baseTranslation: 'notifications.competitor_matches_update',
        });
        this.runMarginInitialisation();
      } catch {
        this.addNotification({
          id: `competitor_matches_update-${Date.now()}`,
          jobStatus: 'failed',
          baseTranslation: 'notifications.competitor_matches_update',
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

::v-deep {
  .main-header th {
    position: relative;
  }

  #buttons-bar {
    max-width: 20rem;
  }

  .v-list-item__title {
    font-size: 1.5rem;
  }

  .v-list-item {
    min-height: 35px;
  }

  .no-data-slot {
    padding-left: 2.2rem;
  }

  .input-screen-page-wrapper .table-cell .tooltipped-truncated-field {
    position: unset;
  }

  .date-attribute-cell {
    position: relative;
  }

  .invalid-blocking {
    border-bottom: 0.2rem solid #c13939 !important;
  }

  .pale {
    opacity: 0.7;
  }

  .workflow-controls {
    .workflow-buttons {
      align-items: center;
    }

    .workflow-info-row {
      margin-top: 4px;
    }
  }

  // match table body cells and last cell of header
  [col-id^='competitor::'].left-competitor-border,
  // match table header group cells
  [col-id*='competitor' i].ag-header-group-cell-with-group {
    border-left: 0.1rem solid var(--ag-border-color);
  }
}
</style>
